import type { DocumentMeta, ViewState } from '../core/types';

import { surfaceLayerIds } from '../core/layerTree';

import { getEngine } from '../engine/engine';

import { fitZoom } from '../gl/view';

import {

  buildStoredDocument,

  deleteStoredDocument,

  layersFromStoredRecord,

  listStoredDocuments,

  packPixels,

  pixelsAreCompressed,

  putStoredDocument,

  unpackPixels,

  type StoredDocument,

  type StoredSurface,

} from '../io/persist';

import { useAppStore } from './store';



/** Debounce so a burst of edits produces one write. */

const AUTOSAVE_DELAY_MS = 1500;

/** Floor between two writes of the same document, since readback is not free. */

const AUTOSAVE_MIN_INTERVAL_MS = 6000;



const engine = getEngine();

const timers = new Map<string, number>();

const lastSaved = new Map<string, number>();

let enabled = true;



function documentById(docId: string): DocumentMeta | null {

  return useAppStore.getState().documents.find((doc) => doc.id === docId) ?? null;

}



async function writeDocument(doc: DocumentMeta): Promise<void> {

  const resources = engine.peek(doc.id);

  if (!resources) return;



  const surfaces: Record<string, StoredSurface> = {};

  for (const layerId of surfaceLayerIds(doc.layers)) {

    const layerResources = resources.layer(layerId);

    if (!layerResources) continue;

    const pixels = await packPixels(layerResources.surface.readPixels());

    const maskPixels = layerResources.mask

      ? await packPixels(layerResources.mask.texture.readPixels())

      : null;

    const smartPixels = layerResources.smartSource

      ? await packPixels(layerResources.smartSource.texture.readPixels())

      : null;

    surfaces[layerId] = { pixels, mask: maskPixels, smartPixels };

  }



  const channelData: Record<string, ArrayBuffer> = {};

  for (const channel of doc.channels) {

    const target = resources.channel(channel.id);

    if (target) channelData[channel.id] = await packPixels(target.texture.readPixels());

  }



  await putStoredDocument(

    buildStoredDocument({

      id: doc.id,

      name: doc.name,

      width: doc.width,

      height: doc.height,

      activeLayerId: doc.activeLayerId,

      layers: doc.layers,

      surfaces,

      channels: doc.channels,

      channelData,

      colorVisible: doc.colorVisible,

      paths: doc.paths,

      activePathId: doc.activePathId,

      guides: doc.guides,

      compressed: pixelsAreCompressed(),

    }),

  );

  lastSaved.set(doc.id, Date.now());

}



/**

 * Queues an autosave for a document. Called after every history commit, so the

 * last state survives a crash, a closed tab or a lost WebGL context.

 */

export function scheduleAutosave(docId: string): void {

  if (!enabled) return;

  const existing = timers.get(docId);

  if (existing) clearTimeout(existing);



  const sinceLast = Date.now() - (lastSaved.get(docId) ?? 0);

  const delay = Math.max(AUTOSAVE_DELAY_MS, AUTOSAVE_MIN_INTERVAL_MS - sinceLast);

  const handle = window.setTimeout(() => {

    timers.delete(docId);

    const doc = documentById(docId);

    if (!doc) return;

    void writeDocument(doc).catch(() => {

      /* autosave is best-effort */

    });

  }, delay);

  timers.set(docId, handle);

}



/** Writes every open document immediately; used before a risky operation. */

export async function autosaveNow(): Promise<void> {

  for (const handle of timers.values()) clearTimeout(handle);

  timers.clear();

  for (const doc of useAppStore.getState().documents) {

    await writeDocument(doc).catch(() => {

      /* autosave is best-effort */

    });

  }

}



export function forgetDocument(docId: string): void {

  const handle = timers.get(docId);

  if (handle) clearTimeout(handle);

  timers.delete(docId);

  lastSaved.delete(docId);

  void deleteStoredDocument(docId);

}



/** Recreates GPU resources for one stored document without touching history. */

async function materialize(record: StoredDocument, addToStore: boolean): Promise<void> {

  const layers = layersFromStoredRecord(record);

  const doc: DocumentMeta = {

    id: record.id,

    name: record.name,

    width: record.width,

    height: record.height,

    layers,

    activeLayerId: record.activeLayerId,

    hasSelection: false,

    channels: record.channels,

    colorVisible: record.colorVisible,

    paths: record.paths,

    activePathId: record.activePathId,

    guides: record.guides,

    dirty: true,

  };

  if (doc.layers.length === 0) return;



  if (addToStore) {

    const view: ViewState = {

      zoom: fitZoom(engine.canvasSize, doc.width, doc.height),

      centerX: doc.width / 2,

      centerY: doc.height / 2,

      rotation: 0,

    };

    useAppStore.getState().addDocument(doc, view);

  } else {

    useAppStore.getState().setDocument(doc);

  }



  const resources = engine.resourcesFor(doc);

  const rect = { x: 0, y: 0, width: doc.width, height: doc.height };

  for (const layerId of surfaceLayerIds(layers)) {

    const surface = record.surfaces[layerId];

    if (!surface) continue;

    const target = resources.ensureLayer(layerId);

    const pixels = await unpackPixels(surface.pixels, record.compressed);

    if (pixels.length === doc.width * doc.height * 4) {

      target.surface.writePixels(rect, pixels);

    }

    if (surface.mask) {

      const mask = resources.ensureMask(layerId);

      const maskPixels = await unpackPixels(surface.mask, record.compressed);

      if (maskPixels.length === doc.width * doc.height) {

        mask.texture.uploadPixels(rect, maskPixels);

      }

    }

    if (surface.smartPixels) {

      const smart = resources.ensureSmartSource(layerId);

      const smartPixels = await unpackPixels(surface.smartPixels, record.compressed);

      if (smartPixels.length === doc.width * doc.height * 4) {

        smart.texture.uploadPixels(rect, smartPixels);

      }

    }

  }



  for (const channel of doc.channels) {

    const buffer = record.channelData[channel.id];

    if (!buffer) continue;

    const pixels = await unpackPixels(buffer, record.compressed);

    if (pixels.length !== doc.width * doc.height) continue;

    resources.ensureChannel(channel.id).texture.uploadPixels(rect, pixels);

  }



  useAppStore.getState().bumpPixelEpoch();

  engine.invalidate();

}



/**
 * A restore is slow enough that a second call can start while the first is still
 * materializing, which React's development double-effect does reliably. Both
 * would then see an empty document list and open every record twice, so the run
 * is shared instead.
 */

let restoring: Promise<number> | null = null;

/** Reopens documents left over from a previous session. Returns how many. */

export function restoreSession(): Promise<number> {

  restoring ??= runRestore().finally(() => {
    restoring = null;
  });

  return restoring;

}

async function runRestore(): Promise<number> {

  if (!engine.available) return 0;

  const records = await listStoredDocuments();

  let restored = 0;

  for (const record of records) {

    // Re-read on every step: opening one document is an await, and a document
    // could have been opened by hand in the meantime.

    if (useAppStore.getState().documents.some((doc) => doc.id === record.id)) continue;

    try {

      await materialize(record, true);

      restored += 1;

    } catch {

      void deleteStoredDocument(record.id);

    }

  }

  return restored;

}



/**

 * Rebuilds GPU state for the documents that are already open, from their last

 * autosave. Used after `webglcontextrestored`, where every texture is gone but

 * the metadata in the store survived.

 */

export async function reloadOpenDocuments(): Promise<void> {

  const records = await listStoredDocuments();

  const byId = new Map(records.map((record) => [record.id, record]));

  const store = useAppStore.getState();



  for (const doc of store.documents) {

    const record = byId.get(doc.id);

    if (record) {

      await materialize(record, false);

      continue;

    }

    // Nothing on disk: keep the layer stack but start from empty pixels.

    engine.resourcesFor(doc);

  }

  engine.invalidate();

}



/** Turns autosave off, e.g. while running under a test harness. */

export function setAutosaveEnabled(value: boolean): void {

  enabled = value;

}

